import { html, css, CSSResultGroup, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { BaseComponent } from './base-component.js'

@customElement('il-dropdown-option')
export default class DropdownOptionComponent extends BaseComponent {
  @property({ type: String }) label: string = ''
  @property({ type: String }) value: string = ''
  @property({ type: String }) tag: string = ''
  @property({ type: Boolean, reflect: true }) selected: boolean = false
  @property({ type: Boolean, reflect: true }) disabled: boolean = false

  override connectedCallback(): void {
    super.connectedCallback()

    this.addEventListener('click', this._onHostClick)
    this.addEventListener('keydown', this._onHostKeydown)

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0')
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()

    this.removeEventListener('click', this._onHostClick)
    this.removeEventListener('keydown', this._onHostKeydown)
  }

  private _onHostClick = (): void => {
    if (this.disabled) return

    this.dispatchEvent(
      new CustomEvent('option-select', {
        detail: { value: this.value, label: this.label, detailText: this.tag },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _onHostKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (this.disabled) return

      e.preventDefault()
      this._onHostClick()
    }
  }

  override render(): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-dropdown-option
          ${this.selected ? 'il-dropdown-option--selected' : ''}
          ${this.disabled ? 'il-dropdown-option--disabled' : ''}
        "
        role="option"
        aria-selected="${this.selected}"
        aria-disabled="${this.disabled}"
        data-test-iliad-dropdown-option
      >
        <div class="il-dropdown-option__left">
          <slot name="icon"></slot>

          <div class="il-dropdown-option__content">
            <span class="il-dropdown-option__label">
              <slot name="label">${this.label}</slot>
            </span>

            <slot name="hint"></slot>

            <slot name="details"></slot>
          </div>
        </div>
      </div>
    `
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      :host {
        display: block;
        cursor: pointer;
        outline: none;
      }

      :host([disabled]) {
        cursor: not-allowed;
      }

      .il-dropdown-option {
        display: flex;
        align-items: flex-start;
        padding: 12px 16px;
        border: 2px solid transparent;
        gap: 8px;
        box-sizing: border-box;
      }

      :host(:hover:not([disabled])) .il-dropdown-option {
        background-color: var(--il-color-accent-50);
      }

      :host(:focus-visible:not([disabled])) .il-dropdown-option {
        border-color: var(--il-color-accent-200);
      }

      .il-dropdown-option.il-dropdown-option--selected .il-dropdown-option__label {
        font-weight: var(--il-font-weight-bold);
      }

      .il-dropdown-option.il-dropdown-option--disabled {
        opacity: 0.5;
      }

      .il-dropdown-option__left {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        flex: 1;
        min-width: 0;
      }

      .il-dropdown-option__content {
        gap: 4px;
        flex: 1;
        min-width: 0;
      }

      .il-dropdown-option__label {
        font-size: var(--il-font-size-16);
        color: var(--il-color-neutrals-350);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-dropdown-option__tag {
        font-size: var(--il-font-size-14);
        color: var(--il-color-neutrals-300);
        border: 1px solid var(--il-color-neutrals-200);
        border-radius: 4px;
        padding: 2px 6px;
        white-space: nowrap;
      }
    `,
  ] as CSSResultGroup
}

declare global {
  interface HTMLElementTagNameMap {
    'il-dropdown-option': DropdownOptionComponent
  }
}
